<template>
    <div class="project-list">

        <heading :title="title" tag="project-list-title">
            <b-button v-if="canCreate"
                      variant="primary"
                      class="ml-3"
                      :to="{name:'projectCreateAgency', params: { agencyId: $user.getAgencyId()}}">
                New Project
                <b-icon-plus-square/>
            </b-button>
            <export-button :fields="exportFields" profile-key="project-list" @export="exportList" />
        </heading>

        <data-table ref="dataTable"
                    persist-name="project-filter"
                    :show-filters="true"
                    :show-pager="true"
                    :get-items="getEntries"
                    v-model="filters"
                    :fields="fields">
            <!-- template for custom columns -->
            <template v-slot:customField="slotProps">
                <div v-if="slotProps.row.field.key === 'actions'" class="action-buttons d-flex">
                    <router-link class="btn btn-secondary btn-sm mr-2 btn-icon"
                                 :to="{ name: 'projectView', params: { projectId : slotProps.row.item.id}}">
                        <b-icon-eye/>
                    </router-link>
                    <auth-template model="project" action="edit" :arg="slotProps.row.item.id">
                        <router-link class="btn btn-secondary btn-sm mr-2 btn-icon"
                                     :to="{ name: 'projectEdit', params: { projectId : slotProps.row.item.id}}">
                            <b-icon-pencil-square/>
                        </router-link>
                    </auth-template>
                    <auth-template model="project" action="delete" :arg="slotProps.row.item.id">
                        <button class="btn btn-danger btn-sm mr-2 btn-icon"
                                v-on:click="removeEntry(slotProps.row.item)">
                            <b-icon-trash/>
                        </button>
                    </auth-template>
                </div>
                <div v-else-if="slotProps.row.field.key === 'projectManager'">
                    <div v-for="manager in slotProps.row.item.projectManager" :key="manager.id">
                        <user-details :user="manager"/>
                    </div>
                </div>
                <div v-else-if="slotProps.row.field.key === 'primeCompanies'">
                    <company-link-list :companies="slotProps.row.item.primeCompanies"/>
                </div>
                <div v-else-if="slotProps.row.field.key === 'subCompanies'">
                    <company-link-list :companies="slotProps.row.item.subCompanies"/>
                </div>
                <div v-else-if="slotProps.row.field.key === 'audit'">
                    <audit-badge :audit="slotProps.row.item.currentAudit"/>
                </div>
                <div v-else-if="slotProps.row.field.key === 'agency'">
                    {{ slotProps.row.item.agency ? slotProps.row.item.agency.name : '' }}
                </div>
                <div v-else-if="slotProps.row.field.key === 'status'">
                    {{ getStatusLabel(slotProps.row.item.status) }}
                </div>
                <div v-else-if="slotProps.row.field.key === 'notes'">
                    <text-popover :id="`popover-${slotProps.row.item.id}`" class="mb-3">
                       {{slotProps.row.item.notes}}
                    </text-popover>
                </div>
                <div v-else>
                    {{ slotProps.row.item[slotProps.row.field.key] }}
                </div>
            </template>
        </data-table>
    </div>
</template>

<script>
import Vue from 'vue'
import Project from '@/models/Project'
import Authorization from '@/providers/Authorization'
import Api from '@/providers/Api'
import Export from '@/providers/Export'
import UserDetails from '@/components/UserDetails'
import CompanyLinkList from '@/components/CompanyLinkList'

export default Vue.extend({
    name: 'ProjectList',
    components: {CompanyLinkList, UserDetails},
    data() {

        return {
            types: [],
            projectFields: Project,
            fields: [
                {key: 'id', sortable: true, label: 'ID'},
                {key: 'name', sortable: true, label: 'Name'},
                {key: 'agency', sortable: true, label: 'Agency'},
                {key: 'notes', sortable: true, label: 'Notes'},
                {key: 'projectManager', sortable: true, label: 'Project Manager'},
                {key: 'primeCompanies', sortable: false, label: 'Prime Contractors'},
                {key: 'subCompanies', sortable: false, label: 'Sub Contractors'},
                {key: 'status', sortable: true, label: 'Status'},
                {key: 'audit', sortable: false, label: 'Current Audit'},
                {key: 'pendingProjectInvitationsCount', sortable: true, label: 'Pending Invites'},
                {key: 'actions', sortable: false}
            ],
            exportFields: {
                name: 'Name',
                description: 'Description',
                agency: 'Agency',
                status: 'Status',
                primeCompanies: 'Prime Contractors',
                subCompanies: 'Subcontractors',
                dollar_value: 'Dollar Value',
                proceed_date: 'Start Date',
                end_date: 'Estimated End Date',
                auditCount: '# of Audits'
            },

            canCreate: false,
            canDelete: false,
        }
    },
    computed: {
        userAgencyId() {
            return this.$user.getAgencyId()
        },
        title() {
            const userAgencyName = this.$user.getAgencyName()
            if (userAgencyName) {
                return `Project List: ${userAgencyName}`
            } else {
                return 'Project List'
            }
        },
        filters: {
            get() {
                const filters = {
                    name: {field: 'name', type: 'string', value: null, label: 'Project Name'},
                    status: {field: 'status', type: 'select', options: {...Project.statusOptions}, label: 'Status'},
                    prime_company_id: {},
                    sub_company_id: {}
                }

                if (this.agencyFilter) {
                    filters.agency_id = this.agencyFilter
                }
                if (this.primeCompanyFilter) {
                    filters.prime_company_id = this.primeCompanyFilter
                }
                if (this.subCompanyFilter) {
                    filters.sub_company_id = this.subCompanyFilter
                }

                return filters
            },
            set(val) {
            }
        },
        agencyFilter() {
            if (this.$user.isEquipmentOwner() || this.$user.isOwnersRepresentative()) {
                return null
            } else {
                return {
                    field: 'agency_id',
                    type: 'select',
                    label: 'Agency',
                    apiTable: 'agency',
                    labelField: 'name',
                    value: null,
                }
            }
        },
        primeCompanyFilter() {
            if (this.$user.isEquipmentOwner()) {
                return null
            } else {
                return {
                    field: 'prime_company_id',
                    type: 'multiselect',
                    search: (query) => {
                        return Api.getItems(this, 'company', {name: {field: 'name', value: query}})
                    },
                    apiTable: 'company',
                    labelField: 'name',
                    value: null,
                    label: 'Prime Company'
                }
            }
        },
        subCompanyFilter() {
            if (this.$user.isEquipmentOwner()) {
                return null
            } else {
                return {
                    field: 'sub_company_id',
                    type: 'multiselect',
                    search: (query) => {
                        return Api.getItems(this, 'company', {name: {field: 'name', value: query}})
                    },
                    apiTable: 'company',
                    labelField: 'name',
                    value: null,
                    label: 'Sub Company'
                }
            }
        }
    },
    methods: {
        getEntries(listParams) {
            return this.$api.getItems(this, 'project', this.filters, listParams, ['agency'])
        },
        getStatusLabel(value) {
            return Project.getStatusLabel(value)
        },
        removeEntry: async function (item) {
            this.$bvModal.msgBoxConfirm('Would you like to delete this project? This action cannot be undone.')
                .then(async value => {
                    if (value) {
                        await this.$api.deleteItem(this, 'project', item)
                        this.$refs.dataTable.refresh()
                    }
                })
        },
        async exportList(type, fields) {
            //  context
            const contextTable = this.$refs.dataTable.getContext()
            const context = contextTable ? {sortBy: contextTable.sortBy, perPage: 100000, currentPage: 1} : null
            this.$api.getItems(this, 'project', this.filters, context, ['agency'])
                .then((projects) => {
                    let callback = (field, value, record) => {
                        if (!value) {
                            return null
                        }
                        if (field === 'status') {
                            return this.getStatusLabel(value)
                        }
                        if (field === 'agency') {
                            return value.name ? value.name : ''
                        }
                        if (field === 'primeCompanies') {
                            let names = ''
                            value.forEach((company, index) => {
                                names += company.name
                                if (index < value.length - 1) {
                                    names += ', '
                                }
                            })
                            return names
                        }
                        if (field === 'subCompanies') {
                            let names = ''
                            value.forEach((company, index) => {
                                names += company.name
                                if (index < value.length - 1) {
                                    names += ', '
                                }
                            })
                            return names
                        }
                        return value
                    }
                    //  export
                    Export.exportRecords(projects, fields, 'Projects', type, callback)
                })
        }
    },
    created() {
        //  authorizations
        Authorization.can({action: 'create', model: 'project'})
            .then((can) => this.canCreate = can)
        // Authorization.can({action: 'delete', model: 'project'})
        //     .then((can) => this.canDelete = can)
    }

})
</script>

<style lang="scss">
.project-list {
    .page-search {
        .col {
            @media(min-width: 576px) {
                flex-basis: auto;
                width: 48%;
            }
            @media(min-width: 991px) {
                width: 32%;
            }
            @media(min-width: 1440px) {
                width: 19%;
            }
        }
    }
}
</style>
